<template>
  <div class="login">
    <div
      id="content"
      class="login__content"
    >
      <div class="login__logo">
        <img
          :src="logo"
          :alt="businessName"
        >
      </div>

      <v-validate-observer
        v-slot="{ handleSubmit }"
        class="login__form"
      >
        <form
          @click.stop
          @key.down.enter="handleSubmit(login)"
          @submit.prevent="handleSubmit(login)"
        >
          <p class="text">
            Para acessar, entre com os seus dados
          </p>

          <v-validate
            v-slot="{ errors }"
            rules="required"
            name="E-mail"
          >
            <base-input
              v-model="form.email"
              type="email"
              tabindex="1"
              :placeholder="businessEmail"
              :error-message="errors[0]"
            >
              <span>E-mail</span>
            </base-input>
          </v-validate>

          <v-validate
            v-slot="{ errors }"
            rules="required"
            name="Senha"
            class="password"
          >
            <base-input
              v-model="form.password"
              :type="password_review ? 'text' : 'password'"
              placeholder="************"
              tabindex="2"
              :error-message="errors[0]"
            >
              <span>Senha</span>
            </base-input>
            <span
              class="icon"
              @click="password_review = !password_review"
            >
              <i
                :class="[
                  'fas',
                  { 'fa-eye-slash': password_review, 'fa-eye': !password_review },
                ]"
              />
            </span>
          </v-validate>

          <div class="field">
            <div class="control is-flex">
              <button
                class="button"
                type="submit"
                tabindex="3"
                :disabled="loading"
              >
                Fazer login
              </button>
            </div>
          </div>

          <the-error
            :message="message.error"
            class="mt-3"
          />
        </form>
      </v-validate-observer>
    </div>
    <span class="login__shadow" />

    <ModalAlertAntifraud
      v-if="alert.show"
      :message="alert.message"
      @close="closeAlertAntifraud"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

import {
  accountsAPI,
  loginAPI,
} from '@/services';

import seo from '@/utils/seo';
import { extract } from '@/utils/message';
import { dropAllContexts } from '@/utils/navigationContext';
import { dropSessionMenu, storeSessionMenu } from '@/utils/sessionMenu';
import loadingMixin from '@/mixins/loadingView.mixin';

import BaseInput from '@/components/Base/Input/index.vue';

export default {
  name: 'LoginView',
  components: {
    BaseInput,
    ModalAlertAntifraud: () => import('@/components/ModalAlertAntifraud.vue'),
  },
  mixins: [
    loadingMixin,
  ],
  data: () => ({
    meta: {
      title: 'Login',
    },
    message: {
      error: '',
      timeout: null,
    },
    form: {
      email: '',
      password: '',
    },
    alert: {
      show: false,
      message: '',
    },
    password_review: false,
    observer: null,
  }),
  computed: {
    ...mapState({
      theme: (state) => state.theme.theme,
      $_userInfo: (state) => state.user.userInfo,
    }),
    userInfo: {
      get() {
        return this.$_userInfo;
      },
      set(payload) {
        this.setUserData(payload);
      },
    },
    businessEmail() {
      return this.theme?.business_info?.business?.email ?? '';
    },
    businessName() {
      return `${this.theme?.name ?? ''}`;
    },
    logo() {
      return `${this.theme?.styles?.logo?.desktop ?? ''}`;
    },
  },
  watch: {
    form: {
      handler() {
        this.message.error = '';
      },
      deep: true,
    },
  },
  created() {
    this.loading = false;
    localStorage.removeItem('params');
  },
  mounted() {
    this.$nextTick(() => this.setObserveContent());
    window.addEventListener('resize', this.setWindowResize);
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('resize', this.setWindowResize);
  },
  methods: {
    ...mapActions({
      setToken: 'app/SET_TOKEN',
      setLoading: 'app/SET_LOADING',
      setUserData: 'user/SET_USER_DATA',
      setChangePasswordRequirement: 'user/SET_CHANGE_PASSWORD_REQUIREMENT',
      setShowCommissionSurvey: 'app/SET_SHOW_SURVEY_COMMISSIONS',
    }),

    // handlers
    handlerError(err) {
      this.$set(this.message, 'error', extract(err));
    },
    setObserveContent() {
      const content = document.getElementById('content');
      const config = { attributes: true, childList: true, subtree: true };
      const vm = this;

      this.observer = new MutationObserver((mutations) => {
        for (const { type } of mutations) {
          if (type === 'childList') {
            vm.setContentHeight(content.clientHeight);
            break;
          }
        }
      });

      this.observer.observe(content, config);
    },
    setWindowResize() {
      const content = document.getElementById('content');
      this.setContentHeight(content.clientHeight);
    },
    setContentHeight(value) {
      document
        .documentElement
        .style
        .setProperty('--login-content-height', value);
    },
    closeAlertAntifraud() {
      this.alert.message = '';
      this.alert.show = false;
    },

    // api
    async getUserInfo() {
      try {
        await accountsAPI
          .getInfoUser()
          .then(({ data }) => {
            this.userInfo = data;

            if (data.filter) localStorage.setItem('reward_report_filter', data.filter);
            else localStorage.removeItem('reward_report_filter');
          });
      } catch {
        // ...
      }
    },
    async login() {
      this.loading = true;

      await loginAPI
        .getToken(this.form)
        .then(async ({ data }) => {
          const {
            token,
            default_route: path,
            quick_access,
            show_survey_commissions,
            force_update_password,
          } = data;

          this.setShowCommissionSurvey(show_survey_commissions);

          // set token
          if (token) {
            // a new session starts with no context, even for the same user as before
            dropAllContexts();
            this.setToken(token);
            await this.setChangePasswordRequirement(force_update_password);
            await this.getUserInfo();
          }

          // set user menu
          if (storeSessionMenu(data)) {
            localStorage.setItem('quick_access', JSON.stringify([
              ...quick_access,
            ]));

            sessionStorage.setItem('hubPriority', 'FGTS');
          } else {
            dropSessionMenu();
            localStorage.removeItem('quick_access');
          }

          // redirect to default intro page
          if (window.location.pathname !== path) this.$router.push({ path }).catch({});
        })
        .catch((err) => {
          if (err?.response.data.error?.errors?.token_antifraud) {
            this.alert.message = err?.response.data.error;
            this.alert.show = true;
          } else {
            this.handlerError(err);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  metaInfo() {
    return seo.meta(this.meta);
  },
};
</script>

<style lang="scss">
.poderfinanceiro {
  .login__logo {
    background-color: $color-secondary !important;
  }
}
</style>

<style lang="scss" scoped>
@mixin size() {
  width: calc(100% - (2 * 3rem));
  max-width: 930px;
  min-height: 50vh;

  @media (min-width: $breakpoint-desktop) {
    min-height: 75vh;
  }
}

@mixin center($top:0px, $left:0px) {
  $base: 50%;

  --top: #{$top};
  --left: #{$left};

  $top-recuo: calc(#{$base} + var(--top));
  $left-recuo: calc(#{$base} + var(--left));

  position: absolute;
  top: $top-recuo;
  left: $left-recuo;

  transform: translate(-50%, -50%);
}

.login {
  $border-radius:1rem;

  position: relative;
  z-index: 1;

  display: flex;
  width: 100vw;
  height: 100vh;
  background-color: $color-primary-750;

  font: {
    family: $font-family;
  }
  align-items: center;

  /** items */
  .text {
    margin-bottom: 1.5em;

    color: $color-secondary;
    line-height: 120%;
    font: {
      weight: bold;
      size: 1.125rem;
    }
  }
  .field {
    margin-top: 2rem;
  }
  .button {
    position: relative;

    width: 100%;
    background-color: $color-primary;
    border-color: rgba(#fff, 0);
    padding: 1rem 2rem;

    color: #333;
    font: {
      family: $font-family;
      weight: bold;
    }

    &:hover {
      &::before {
        content: '';

        position: absolute;
        z-index: 1;

        display: block;
        width: 100%;
        height: 100%;
        background-color: rgba(#000, .10);

        pointer-events: none;
      }
    }

    @media (min-width: $breakpoint-desktop) {
      margin-left: auto;
      width: fit-content;
    }
  }

  /** box */
  &__content {
    @include size();
    @include center();

    z-index: 1;

    display: grid;

    grid-template-areas: "logo" "form";
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;

    @media (min-width: $breakpoint-desktop) {
      grid-template-areas: "logo form";
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr;
    }
  }
  &__shadow {
    @include size();
    @include center(5px);

    z-index: 0;

    display: grid;
    min-width: 220px;
    height: calc(var(--login-content-height) * 1px);
    border-radius: $border-radius;
    background-color: rgba(#000, .25);

    @media (min-width: $breakpoint-desktop) {
      @include center(5px, 5px);
    }
  }

  /** sides */
  &__logo,
  &__form {
    padding: 2rem;

    @media (min-width: $breakpoint-desktop) {
      padding: 3rem;
    }
  }
  &__logo {
    display: flex;
    background-color: #fff;
    border-radius: $border-radius $border-radius 0 0;

    align-items: center;

    img {
      margin: 0 auto;

      width: 100%;
      max-width: 175px;
    }

    @media (min-width: $breakpoint-desktop) {
      background-color: $color-light;
      border-radius: $border-radius 0 0 $border-radius;

      img {
        max-width: 300px;
      }
    }
  }
  &__form {
    display: flex;
    background-color: $color-light;
    border-radius: 0 0 $border-radius $border-radius;

    justify-content: center;
    align-items: center;

    > form {
      width: 100%;

      > span {
        margin-bottom: 1rem;

        display: block;
      }
    }

    &::v-deep {
      label {
        color: $color-secondary;
      }
    }

    @media (min-width: $breakpoint-desktop) {
      background-color: #fff;
      border-radius: 0 $border-radius $border-radius 0;
    }
  }
}

.password {
  position: relative;

  .icon {
    position: absolute;

    top: calc(50% - .15rem);
    right: .65rem;
    z-index: 1;

    padding: .5rem;

    cursor: pointer;
  }
}
</style>
